import {
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  ShoppingCart as ShoppingCartIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Currency from "../../common/Currency";

interface OrderSummaryProps {
  userFirstName: string;
  userLastName: string;
  userId: string;
  userEmail: string;
  userPhoneNumber: string;
  userCountry: string;
  userProfilePicture: string;
  deliveryInfo: {
    fullName: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phone: string;
    email: string;
    company?: string;
    address2?: string;
  };
  paymentInfo: {
    paymentMethod: string;
    transactionId: string;
    paymentDate?: string;
    cardLast4: string;
    cardBrand: string;
    paymentStatus: string;
    amountPaid: number;
    amountPaidTjs?: number | null;
    refundAmount: number;
  };
  totalAmount: number;
  shippingCost: number;
  tax: number;
  fees?: number | null;
  discountAmount?: number | null;
  finalTotal: number;
  itemCount: number;
  // TJS (Somoni) conversion
  exchangeRateAtOrder?: number | null;
  totalAmountTjs?: number | null;
  shippingCostTjs?: number | null;
  taxTjs?: number | null;
  feesTjs?: number | null;
  discountAmountTjs?: number | null;
  finalTotalTjs?: number | null;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  userFirstName,
  userLastName,
  userId,
  userEmail,
  userPhoneNumber,
  userCountry,
  userProfilePicture,
  deliveryInfo,
  paymentInfo,
  totalAmount,
  shippingCost,
  tax,
  fees,
  discountAmount,
  finalTotal,
  itemCount,
  exchangeRateAtOrder,
  totalAmountTjs,
  shippingCostTjs,
  taxTjs,
  feesTjs,
  discountAmountTjs,
  finalTotalTjs,
}) => {
  const { t } = useTranslation();
  const [expandCustomerInfo, setExpandCustomerInfo] = useState(false);
  const [expandDeliveryInfo, setExpandDeliveryInfo] = useState(false);
  const [expandPaymentInfo, setExpandPaymentInfo] = useState(false);
  const [expandOrderSummary, setExpandOrderSummary] = useState(true);

  // Fallback: compute TJS from stored exchange rate if dedicated TJS fields are null (old orders)
  const rate = exchangeRateAtOrder;
  const computeTjs = (usd: number | null | undefined): number | null => {
    if (usd == null || !rate) return null;
    return Math.round(usd * rate * 100) / 100;
  };
  const effectiveTotalAmountTjs = totalAmountTjs ?? computeTjs(totalAmount);
  const effectiveShippingCostTjs = shippingCostTjs ?? computeTjs(shippingCost);
  const effectiveTaxTjs = taxTjs ?? computeTjs(tax);
  const effectiveFeesTjs = feesTjs ?? computeTjs(fees);
  const effectiveDiscountAmountTjs = discountAmountTjs ?? computeTjs(discountAmount);
  const effectiveFinalTotalTjs = finalTotalTjs ?? computeTjs(finalTotal);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "success.main";
      case "pending":
        return "warning.main";
      case "failed":
        return "error.main";
      default:
        return "text.secondary";
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Customer Information */}
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardContent sx={{ p: 0, m: 0 }}>
            {/* Customer Info Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                cursor: "pointer",
                "&:hover": { bgcolor: "grey.50" },
              }}
              onClick={() => setExpandCustomerInfo(!expandCustomerInfo)}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                {t("admin.customerInformation")}
              </Typography>
              <Tooltip
                title={
                  expandCustomerInfo ? t("admin.collapseSection") : t("admin.expandSection")
                }
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandCustomerInfo(!expandCustomerInfo);
                  }}
                  color="primary"
                  sx={{
                    transform: expandCustomerInfo
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.2s ease-in-out",
                    alignSelf: "center",
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Customer Info Content */}
            <Collapse in={expandCustomerInfo} timeout={200} sx={{ m: 0, p: 0 }}>
              <Box sx={{ px: 2, pb: 0 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                  {userProfilePicture ? (
                    <Avatar
                      src={userProfilePicture}
                      alt={`${userFirstName} ${userLastName}`}
                      sx={{
                        width: 80,
                        height: 80,
                        mr: 2,
                        border: "3px solid",
                        borderColor: "primary.main",
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mr: 2,
                        bgcolor: "primary.main",
                        fontSize: "1.5rem",
                        border: "3px solid",
                        borderColor: "primary.light",
                      }}
                    >
                      {userFirstName?.[0]}
                      {userLastName?.[0]}
                    </Avatar>
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {userFirstName} {userLastName}
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {t("admin.id")}: {userId}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {t("admin.email")}: {userEmail}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {t("admin.phone")}: {userPhoneNumber}
                      </Typography>
                      {userCountry && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {t("admin.country")}: {userCountry}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Collapse>

            {/* Delivery Address Header */}
            <Divider />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                cursor: "pointer",
                "&:hover": { bgcolor: "grey.50" },
              }}
              onClick={() => setExpandDeliveryInfo(!expandDeliveryInfo)}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                <LocationIcon sx={{ mr: 1, color: "primary.main" }} />
                {t("admin.deliveryAddress")}
              </Typography>
              <Tooltip
                title={
                  expandDeliveryInfo ? t("admin.collapseSection") : t("admin.expandSection")
                }
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandDeliveryInfo(!expandDeliveryInfo);
                  }}
                  color="primary"
                  sx={{
                    transform: expandDeliveryInfo
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.2s ease-in-out",
                    alignSelf: "center",
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Delivery Address Content */}
            <Collapse in={expandDeliveryInfo} timeout={200} sx={{ m: 0, p: 0 }}>
              <Box sx={{ px: 2, pb: 0 }}>
                {/* Main Address Information */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
                    >
                      {deliveryInfo?.fullName || "N/A"}
                    </Typography>
                    {deliveryInfo?.company && (
                      <Typography
                        variant="body2"
                        color="primary.main"
                        sx={{ mb: 1, fontStyle: "italic" }}
                      >
                        {deliveryInfo.company}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {deliveryInfo?.address || "N/A"}
                    </Typography>
                    {deliveryInfo?.address2 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {deliveryInfo.address2}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {deliveryInfo?.city || "N/A"},{" "}
                      {deliveryInfo?.state || "N/A"}{" "}
                      {deliveryInfo?.zipCode || "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0 }}
                    >
                      {deliveryInfo?.country || "N/A"}
                    </Typography>
                  </Box>
                </Box>

                {/* Contact Information */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "primary.50",
                      border: "1px solid",
                      borderColor: "primary.200",
                    }}
                  >
                    <PhoneIcon fontSize="small" color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {deliveryInfo?.phone || "N/A"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "primary.50",
                      border: "1px solid",
                      borderColor: "primary.200",
                    }}
                  >
                    <EmailIcon fontSize="small" color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {deliveryInfo?.email || userEmail || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Grid>

      {/* Order Summary & Payment */}
      <Grid item xs={12} md={6}>
        <Card elevation={2}>
          <CardContent sx={{ p: 0, m: 0 }}>
            {/* Order Summary Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                cursor: "pointer",
                "&:hover": { bgcolor: "grey.50" },
              }}
              onClick={() => setExpandOrderSummary(!expandOrderSummary)}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                <ShoppingCartIcon sx={{ mr: 1, color: "primary.main" }} />
                {t("admin.orderSummary")}
              </Typography>
              <Tooltip
                title={
                  expandOrderSummary ? t("admin.collapseSection") : t("admin.expandSection")
                }
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandOrderSummary(!expandOrderSummary);
                  }}
                  color="primary"
                  sx={{
                    transform: expandOrderSummary
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.2s ease-in-out",
                    alignSelf: "center",
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Order Summary Content */}
            <Collapse in={expandOrderSummary} timeout={200} sx={{ m: 0, p: 0 }}>
              <Box sx={{ px: 2, pb: 0 }}>
                <Box sx={{ mb: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t("admin.items")} ({itemCount}):
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      <Currency amount={totalAmount} tjsAmount={effectiveTotalAmountTjs} />
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography variant="body2">{t("admin.shipping")}:</Typography>
                    <Typography variant="body2">
                      <Currency amount={shippingCost} tjsAmount={effectiveShippingCostTjs} />
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography variant="body2">{t("admin.tax")}:</Typography>
                    <Typography variant="body2">
                      <Currency amount={tax || 0} tjsAmount={effectiveTaxTjs} />
                    </Typography>
                  </Box>
                  {fees != null && fees > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "grey.200",
                      }}
                    >
                      <Typography variant="body2">{t("admin.fees")}:</Typography>
                      <Typography variant="body2">
                        <Currency amount={fees} tjsAmount={effectiveFeesTjs} />
                      </Typography>
                    </Box>
                  )}
                  {discountAmount != null && discountAmount > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "success.50",
                        border: "1px solid",
                        borderColor: "success.200",
                      }}
                    >
                      <Typography variant="body2" color="success.main">
                        {t("admin.discount")}:
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        -<Currency amount={discountAmount} tjsAmount={effectiveDiscountAmountTjs} />
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: "bold",
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "primary.50",
                      border: "1px solid",
                      borderColor: "primary.200",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t("admin.total")}:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "primary.main" }}
                    >
                      <Currency amount={finalTotal} tjsAmount={effectiveFinalTotalTjs} />
                    </Typography>
                  </Box>
                  {exchangeRateAtOrder && (
                    <Box sx={{ mt: 1, textAlign: "right" }}>
                      <Typography variant="caption" color="text.secondary">
                        {t("admin.exchangeRate")}: 1 USD = {exchangeRateAtOrder} c.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>

            {/* Payment Information Header */}
            <Divider />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                cursor: "pointer",
                "&:hover": { bgcolor: "grey.50" },
              }}
              onClick={() => setExpandPaymentInfo(!expandPaymentInfo)}
            >
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                <PaymentIcon sx={{ mr: 1, color: "primary.main" }} />
                {t("admin.paymentInformation")}
              </Typography>
              <Tooltip
                title={
                  expandPaymentInfo ? t("admin.collapseSection") : t("admin.expandSection")
                }
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandPaymentInfo(!expandPaymentInfo);
                  }}
                  color="primary"
                  sx={{
                    transform: expandPaymentInfo
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.2s ease-in-out",
                    alignSelf: "center",
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Payment Information Content */}
            <Collapse in={expandPaymentInfo} timeout={200} sx={{ m: 0, p: 0 }}>
              <Box sx={{ px: 2, pb: 0 }}>
                {/* Payment Method & Status */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.paymentMethod")}:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {paymentInfo?.paymentMethod || "N/A"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.status")}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: getStatusColor(
                            paymentInfo?.paymentStatus || "",
                          ),
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: `${getStatusColor(paymentInfo?.paymentStatus || "")}20`,
                        }}
                      >
                        {paymentInfo?.paymentStatus || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Transaction Details */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "primary.50",
                      border: "1px solid",
                      borderColor: "primary.200",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.transactionId")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontFamily: "monospace" }}
                    >
                      {paymentInfo?.transactionId || "N/A"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "success.50",
                      border: "1px solid",
                      borderColor: "success.200",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.amountPaid")}:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: "success.main" }}
                    >
                      <Currency amount={paymentInfo?.amountPaid || 0} tjsAmount={paymentInfo?.amountPaidTjs} />
                    </Typography>
                  </Box>

                  {paymentInfo?.paymentDate && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "info.50",
                        border: "1px solid",
                        borderColor: "info.200",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.paymentDate")}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(paymentInfo.paymentDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}

                  {paymentInfo?.cardLast4 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "warning.50",
                        border: "1px solid",
                        borderColor: "warning.200",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.cardDetails")}:
                      </Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontFamily: "monospace" }}
                        >
                          **** **** **** {paymentInfo.cardLast4}
                        </Typography>
                        {paymentInfo?.cardBrand && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontStyle: "italic" }}
                          >
                            {paymentInfo.cardBrand}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default OrderSummary;
